/**
 * useLivekitToken — TV-P3-04
 *
 * Fetches a LiveKit room token for the current encounter by calling
 * the generateLivekitToken Cloud Function with the active Keycloak
 * bearer. The Cloud Function verifies the bearer + mints a 1-hour
 * room JWT shaped to the caller's role.
 *
 * Returns `{ token, url, isLoading, error, refresh }`:
 *   - `token` and `url` are passed straight to <LiveKitRoom>.
 *   - `refresh()` re-fetches (use when LiveKit emits Disconnected
 *     with reason=TOKEN_EXPIRED).
 *
 * Caching:
 *   - Tokens have a 60-min TTL on the server. We don't aggressively
 *     refresh; LiveKit's SDK pushes a disconnect when it stops being
 *     valid, and the caller invokes refresh() then.
 *
 * Failure modes:
 *   - `firebaseFunctions` is null when Firebase init failed (offline
 *     dev). We surface that as an error rather than NPE'ing.
 *   - The Cloud Function returns 401 if the KC bearer doesn't verify
 *     against the realm JWKS — usually means the user's KC session
 *     lapsed. Caller can prompt re-login.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { firebaseFunctions } from '@/firebase/config';
import { useAuth } from '@/auth/AuthProvider';

export function useLivekitToken({
  roomName,
  participantName,
  participantRole,
} = {}) {
  const { token: accessToken } = useAuth();

  const [state, setState] = useState({
    token: null,
    url: null,
    isLoading: !!roomName,
    error: null,
  });

  // Latch to prevent duplicate fetches on StrictMode double-render.
  const fetchingRef = useRef(false);

  const fetchToken = useCallback(async () => {
    if (!roomName || !participantName) {
      setState({ token: null, url: null, isLoading: false, error: null });
      return;
    }
    if (!firebaseFunctions) {
      setState({
        token: null,
        url: null,
        isLoading: false,
        error: new Error(
          'Firebase is not initialized — VITE_FIREBASE_* env vars missing.',
        ),
      });
      return;
    }
    if (!accessToken) {
      setState({
        token: null,
        url: null,
        isLoading: false,
        error: new Error(
          'No Keycloak access token in session — cannot mint a LiveKit token.',
        ),
      });
      return;
    }
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const callable = httpsCallable(firebaseFunctions, 'generateLivekitToken');
      const result = await callable({
        keycloakToken: accessToken,
        roomName,
        participantName,
        participantRole, // optional; server falls back to KC-role inference
      });
      const { token, url } = result?.data || {};
      if (!token || !url) {
        throw new Error('Cloud Function returned no token/url');
      }
      setState({ token, url, isLoading: false, error: null });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[useLivekitToken] fetch failed:', err);
      setState({
        token: null,
        url: null,
        isLoading: false,
        error: err instanceof Error ? err : new Error(String(err)),
      });
    } finally {
      fetchingRef.current = false;
    }
  }, [accessToken, roomName, participantName, participantRole]);

  useEffect(() => {
    fetchToken();
  }, [fetchToken]);

  return { ...state, refresh: fetchToken };
}
